import {
  Card,
  withStyles,
  CardTabs,
  InterestAccount,
  SupportedCurrencies,
  BodyText,
  InterestTerm,
  InterestRate,
} from 'vopay';
import { useState, useMemo } from 'react';
import styles from './InterestAccountDrawer.module.scss';
import TransactionsTab from './TransactionsTab';
import InterestHistoryTab from './InterestHistoryTab';
import UpgradePlanDialog from './UpgradePlanDialog/UpgradePlanDialog';
import InterestAccountFundTransferModal from './InterestAccountFundTransferModal/InterestAccountFundTransferModal';
import { usePermissions } from '@/providers/Permissions.provider';
import { Title, Button, DropdownMenu, CircleButton, SideDrawer, Dialog } from 'UI';
import { useTranslation } from 'react-i18next';
import InterestAccountCard from './InterestAccountCard';
import TierPips from './TierPips/TierPips';
import AnnualRatesTooltip from '@/components/Accounts/AccountSummary/AccountUI/Tooltip/AnnualRatesTooltip';
import enablePremiumIcon from './enablePremiumIcon.svg';

const cn = withStyles(styles);

type InterestAccountsDrawerProps = {
  open: boolean;
  interestAccounts: InterestAccount[];
  interestRates: InterestRate[];
  currency: SupportedCurrencies;
  onClose: () => void;
  onTransfer: () => void;
};

export default function InterestAccountsDrawer({
  open,
  interestAccounts,
  interestRates,
  currency,
  onClose,
  onTransfer,
}: InterestAccountsDrawerProps) {
  const { t } = useTranslation('translations', { keyPrefix: 'Accounts.InterestAccountsDrawer' });
  const { permissions } = usePermissions();
  const [upgradePlanDialogOpen, setUpgradePlanDialogOpen] = useState(false);

  const standardAccount = useMemo<InterestAccount>(() => {
    return (
      Object.values(interestAccounts).find((account) => account.InterestMaturityType === 'short-term') || {
        AccountName: '',
        InterestMaturityType: 'short-term',
        CurrentBalance: 0,
        InterestAccrued: 0,
        EligibleBalance: 0,
      }
    );
  }, [interestAccounts]);

  const premiumAccount = useMemo<InterestAccount | null>(() => {
    return Object.values(interestAccounts).find((account) => account.InterestMaturityType === 'long-term') || null;
  }, [interestAccounts]);

  const [transferDialogOpen, setTransferDialogOpen] = useState(false);
  const [transferType, setTransferType] = useState<'Fund' | 'Withdraw' | null>(null);
  const standardInterestRates = getInterestRate('short-term', standardAccount.EligibleBalance);
  const premiumInterestRates = getInterestRate('long-term', premiumAccount?.EligibleBalance || 0);

  return (
    <>
      <SideDrawer
        isOpen={open}
        onClose={() => {
          setTransferType(null);
          onClose();
        }}
        title={t('drawerTitle', { defaultValue: 'Interest Details' })}
        style={{
          width: 'calc(100% - var(--navLeftWidthSmall, 80px))',
        }}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <div className={cn('interestAccountsDrawer')}>
          <div className={cn('topCardsContainer')}>
            <InterestAccountCard
              title={
                <div className={cn('interestAccountCardTitle')}>
                  <div>{t('standard', { defaultValue: 'Standard' })}</div>

                  <AnnualRatesTooltip
                    accountType="standard"
                    balance={Number(standardAccount.EligibleBalance)}
                    interestRates={interestRates}
                    align="start"
                    trigger={
                      <TierPips
                        numTiers={standardInterestRates.tiers}
                        tierLevel={standardInterestRates.tierLevel}
                        percent={standardInterestRates.annualInterestRate}
                      />
                    }
                  />
                </div>
              }
              currency={currency}
              account={standardAccount}
            />

            {premiumAccount !== null ? (
              <InterestAccountCard
                className={cn('premiumCard')}
                title={
                  <div className={cn('interestAccountCardTitle')}>
                    <div className={cn('premiumTextGradient')}>{t('premium', { defaultValue: 'Premium' })}</div>

                    <AnnualRatesTooltip
                      accountType="premium"
                      balance={Number(premiumAccount.EligibleBalance)}
                      interestRates={interestRates}
                      trigger={
                        <TierPips
                          numTiers={premiumInterestRates.tiers}
                          tierLevel={premiumInterestRates.tierLevel}
                          percent={premiumInterestRates.annualInterestRate}
                          isPremium
                        />
                      }
                    />

                    {!!permissions.ManageInterestAccounts && (
                      <div className={cn('premiumAccountDropdown')}>
                        <DropdownMenu>
                          <DropdownMenu.Trigger className={cn('trigger')} asChild>
                            <CircleButton
                              icon="pending"
                              iconFill
                              colorScheme="action"
                              size="large"
                              circleFill={false}
                            />
                          </DropdownMenu.Trigger>

                          <DropdownMenu.Content align="end">
                            <DropdownMenu.Item
                              onSelect={() => {
                                setTransferType('Fund');
                                setTransferDialogOpen(true);
                              }}
                            >
                              {t('addFunds', { defaultValue: 'Add Funds' })}
                            </DropdownMenu.Item>
                            <DropdownMenu.Item
                              onSelect={() => {
                                setTransferType('Withdraw');
                                setTransferDialogOpen(true);
                              }}
                            >
                              {t('withdraw', { defaultValue: 'Withdraw' })}
                            </DropdownMenu.Item>
                          </DropdownMenu.Content>
                        </DropdownMenu>
                      </div>
                    )}
                  </div>
                }
                currency={currency}
                account={premiumAccount}
              />
            ) : (
              <Card className={cn('enablePremiumCard', 'premiumCard')}>
                <div>
                  <img src={enablePremiumIcon} />
                </div>

                <div>
                  <Title level="4" className={cn('enablePremiumCardTitle', 'premiumTextGradient')}>
                    {t('enablePremiumCardTitle', { defaultValue: 'Premium Plan' })}
                  </Title>

                  <BodyText className={cn('enablePremiumCardDesc')}>
                    {t('enablePremiumCardDesc', {
                      defaultValue:
                        'Earn a higher annual interest rate on your end-of-day balance with a longer lock-in period for greater returns.',
                    })}
                  </BodyText>

                  <Button
                    disabled={!permissions.ManageInterestAccounts}
                    colorScheme="feature"
                    iconRightProps={{
                      icon: 'auto_awesome',
                      fillAmount: 1,
                    }}
                    onClick={() => {
                      setUpgradePlanDialogOpen(true);
                    }}
                  >
                    {t('enablePremium', { defaultValue: 'Enable Premium' })}
                  </Button>
                </div>
              </Card>
            )}
          </div>

          <div className={cn('cardTabsContainer')}>
            <CardTabs.Root defaultValue="transaction" className={cn('cardTabs')}>
              <CardTabs.List aria-label="Interest Account">
                <CardTabs.Trigger value="transaction">
                  <span>{t('transactions', { defaultValue: 'Transactions' })}</span>
                </CardTabs.Trigger>

                <CardTabs.Trigger value="interest">
                  <span>{t('interest', { defaultValue: 'Interest' })}</span>
                </CardTabs.Trigger>
              </CardTabs.List>

              <CardTabs.Content value="transaction" className={cn('cardTabContent')} forceMount>
                <TransactionsTab hasLongTermAccount={premiumAccount !== null} currency={currency} />
              </CardTabs.Content>

              <CardTabs.Content value="interest" className={cn('cardTabContent')} forceMount>
                <InterestHistoryTab hasLongTermAccount={premiumAccount !== null} currency={currency} />
              </CardTabs.Content>
            </CardTabs.Root>
          </div>

          {transferType && (
            <InterestAccountFundTransferModal
              transferType={transferType}
              visible={transferDialogOpen}
              currentBalance={
                transferType === 'Fund' ? standardAccount.CurrentBalance : premiumAccount?.CurrentBalance || 0
              }
              onClose={(added) => {
                setTransferDialogOpen(false);

                if (added) {
                  onTransfer();
                }
              }}
              currency={currency}
              formatAmount={formatAmount}
            />
          )}
        </div>
      </SideDrawer>

      <UpgradePlanDialog open={upgradePlanDialogOpen} onClose={() => setUpgradePlanDialogOpen(false)} />
    </>
  );

  function formatAmount(number: string) {
    if (Number.isNaN(parseFloat(number)) || number === '' || number === undefined) return '0.00';

    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(parseFloat(number));
  }

  function getInterestRate(
    term: InterestTerm,
    balance: number,
  ): { annualInterestRate: number; tierLevel: number; tiers: number } {
    const applicableRates = interestRates.filter((rate) => rate.Term === term);

    if (applicableRates.length === 0) {
      // If no applicable rates are found, return 0
      return {
        annualInterestRate: 0,
        tierLevel: 0,
        tiers: 4,
      };
    }

    const matchingRate = applicableRates.find((rate) => balance >= rate.RangeStart && balance <= rate.RangeEnd);

    if (matchingRate) {
      return {
        annualInterestRate: matchingRate.AnnualInterestRate / 100,
        tierLevel: applicableRates.indexOf(matchingRate) + 1, // add 1 to make it 1-based; 0 if it doesn't belong anywhere.
        tiers: applicableRates.length,
      };
    }

    // Check if balance exceeds the last tier's range
    const lastRate = applicableRates[applicableRates.length - 1];

    if (lastRate && balance > lastRate.RangeEnd) {
      return {
        annualInterestRate: lastRate.AnnualInterestRate / 100,
        tierLevel: applicableRates.length,
        tiers: applicableRates.length,
      };
    }

    // If balance is below the first tier's range, return 0
    return {
      annualInterestRate: 0,
      tierLevel: 0,
      tiers: applicableRates.length,
    };
  }
}
